import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ALL_LESSONS } from "@/data/lessons";
import {
  AccountShell,
  PageHeader,
  mono,
  serif,
} from "@/components/AccountLayout";
import { useCompletions } from "@/hooks/useCompletions";
import { getMyAccount } from "@/lib/account.functions";

export const Route = createFileRoute("/account")({
  head: () => {
    const title = "Your account — WHO KNEW";
    const description =
      "Manage your WHO KNEW account, view your purchase, track your progress, or get in touch.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: "/account" },
      ],
    };
  },
  component: AccountPage,
});

function AccountPage() {
  const navigate = useNavigate();
  const completed = useCompletions();
  const [email, setEmail] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getMyAccount()
      .then((res) => {
        if (cancelled) return;
        if (!res.ok) {
          if (res.error === "no_session") navigate({ to: "/" });
          return;
        }
        setEmail(res.email);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const total = ALL_LESSONS.length;
  const doneCount = ALL_LESSONS.reduce((n, l) => n + (completed.has(l.id) ? 1 : 0), 0);
  const progressDesc =
    doneCount === 0
      ? "Not started yet."
      : doneCount === total
        ? "Course complete."
        : `${doneCount} of 29 lessons complete.`;


  const rows: Array<{ eyebrow: string; description: string; to: string }> = [
    {
      eyebrow: "WHAT YOU BOUGHT",
      description: "Your purchase and access details.",
      to: "/account/purchase",
    },
    {
      eyebrow: "YOUR DETAILS",
      description: "Email and account information.",
      to: "/account/details",
    },
    { eyebrow: "YOUR PROGRESS", description: progressDesc, to: "/account/progress" },
    {
      eyebrow: "CONTACT US",
      description: "Get help or send us a note.",
      to: "/account/contact",
    },
  ];

  return (
    <AccountShell>
      <PageHeader eyebrow="YOUR ACCOUNT" title="Your account." />

      <div style={{ marginTop: "48px" }}>
        {rows.map((row, idx) => (
          <div key={row.to}>
            {idx > 0 && <div style={{ height: "1px", backgroundColor: "#E8E1D3" }} />}
            <Link
              to={row.to}
              className="block group transition-colors duration-200"
              style={{ padding: "20px 20px" }}
              activeProps={{}}
            >
              <div className="flex items-center justify-between gap-4 group-hover:[&_*]:transition-colors">
                <div className="flex-1 min-w-0">
                  <p
                    className="font-bold uppercase tracking-[0.05em] text-burgundy"
                    style={{ ...mono, fontSize: "11px" }}
                  >
                    {row.eyebrow}
                  </p>
                  <p
                    className="text-ink"
                    style={{ ...serif, fontSize: "16px", marginTop: "8px" }}
                  >
                    {row.description}
                  </p>
                </div>
              </div>
            </Link>
          </div>
        ))}
        <div style={{ height: "1px", backgroundColor: "#E8E1D3" }} />
      </div>

      <style>{`
        .block.group:hover { background-color: #E8E1D3; }
      `}</style>

      <div style={{ marginTop: "48px", paddingBottom: "96px" }}>
        <p style={{ ...serif, fontSize: "16px", fontStyle: "italic", color: "#1A1612" }}>
          {email ? `Signed in as ${email}` : "Signed in"}
          <span style={{ color: "#8B1E1E" }}> · </span>
          <button
            type="button"
            onClick={async () => {
              try {
                const { supabase } = await import("@/integrations/supabase/client");
                const { data } = await supabase.auth.getSession();
                const email = data.session?.user?.email;
                if (email) {
                  try {
                    window.localStorage.setItem("last_signed_in_email", email);
                  } catch {}
                }
                await supabase.auth.signOut();
              } catch {}
              // Clear device-scoped per-user state so the next sign-in on
              // this browser doesn't inherit ghost ticks / cached resume
              // pointer from the previous account. Server is the source of
              // truth — these keys are write-through caches only.
              try {
                window.localStorage.removeItem("wk-completed-lessons");
                window.localStorage.removeItem("wk-completed-quizzes");
                window.localStorage.removeItem("wk-migrated-completions");
                window.localStorage.removeItem("wk-last-lesson");
                window.localStorage.removeItem("wk-course-complete");
                window.localStorage.removeItem("wk-orientation-dismissed");
              } catch {}
              window.location.assign("/?signed_out=1");
            }}

            className="italic text-burgundy underline decoration-burgundy/40 decoration-1 underline-offset-4 hover:decoration-2 bg-transparent cursor-pointer p-0"
            style={{ ...serif, fontSize: "16px" }}
          >
            Sign out
          </button>
        </p>
      </div>

    </AccountShell>
  );
}
